import React, { createContext, useContext, useEffect, useState } from 'react';
import { useApp } from './AppContext';
import { readQs, writeQs } from '../../../../../../../shared/lib/querystring';
import { useExperienceById } from '../../../hooks/useExperienceById';
import { FullScreenLoader } from '../components/FullScreenLoader';
import { ReservablePipelineEvents } from '../../../../../../../shared/lib/reserve';
import { useExperiences } from '../../../hooks/useExperiences';
import Alert from '../components/Alert';
import { ReservableEventWithTimeslots } from '../../../../../../../shared/types/reservable';

interface EventCacheContextProps {
  eventId: string | null;
  event: ReservableEventWithTimeslots | null;
  setEventId: (id: string | null) => void;
  allEvents: ReservablePipelineEvents | null;
  fetchAllEvents: () => void;
  setEventDateRange: (startDate?: string, endDate?: string) => void;
  loading: boolean;
}

const EventCacheContext = createContext<EventCacheContextProps | undefined>(undefined);

interface EventBundle {
  _eventId: string | null;
  event: ReservableEventWithTimeslots | null;
}

interface WithEventCachePropsBase {}

type WithEventCacheProps = React.PropsWithChildren<WithEventCachePropsBase>;

export function WithEventCache(props: WithEventCacheProps) {
  const qs = readQs();

  const { config } = useApp();
  const { propertyId } = config;
  const [eventBundle, _setEventBundle] = useState<EventBundle>({
    _eventId: qs.go_event ? String(qs.go_event) : null,
    event: null,
  });

  const [eventStartDate, setEventStartDate] = useState<string | undefined>(undefined);
  const [eventEndDate, setEventEndDate] = useState<string | undefined>(undefined);

  const eventHook = useExperienceById(propertyId, eventBundle._eventId || '', {
    enabled: !!eventBundle._eventId,
  });
  const eventsHook = useExperiences(
    { propertyId, startDate: eventStartDate, endDate: eventEndDate },
    {
      refetchOnMount: true,
    }
  );

  function setEventBundle(newEvent: EventBundle) {
    _setEventBundle(newEvent);

    if (newEvent._eventId) {
      writeQs({ ...readQs(), go_event: newEvent._eventId });
    } else {
      const q = readQs();
      delete q.go_event;
      writeQs(q);
    }
  }

  // Update local event state when eventHook data changes
  useEffect(() => {
    if (eventHook.data && eventBundle._eventId) {
      setEventBundle({
        _eventId: eventBundle._eventId,
        event: eventHook.data,
      });
    }
  }, [eventHook.data, eventBundle._eventId]);

  return (
    <EventCacheContext.Provider
      value={{
        eventId: eventBundle._eventId,
        setEventId: (id: string | null) => setEventBundle({ _eventId: id, event: null }),
        event: eventBundle.event ?? eventHook.data ?? null,
        allEvents: eventsHook.data || null,
        fetchAllEvents: eventsHook.refetch,
        setEventDateRange: (startDate?: string, endDate?: string) => {
          setEventStartDate(startDate);
          setEventEndDate(endDate);
        },
        loading: eventHook.isLoading || eventsHook.isLoading,
      }}
    >
      <div className="relative">{props.children}</div>
    </EventCacheContext.Provider>
  );
}

export function useEventCache() {
  const context = useContext(EventCacheContext);
  if (context === undefined) {
    throw new Error('useEventCache must be used within a EventCacheProvider');
  }
  return context;
}

interface ForceEventOrFallbackErrorProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export function ForceEventOrFallbackError({ children, fallback }: ForceEventOrFallbackErrorProps) {
  const { event, loading } = useEventCache();

  if (loading) {
    return <FullScreenLoader className="border-t border-border" />;
  }

  if (!event) {
    return (
      <div className="p-6 bg-white">
        {fallback || (
          <Alert>
            Unable to load the experience. Please refresh the page and try again. Some items may no
            longer be available.
          </Alert>
        )}
      </div>
    );
  }

  return <>{children}</>;
}

type ForcedEventContextProps = EventCacheContextProps & { event: ReservableEventWithTimeslots };

export function useForcedEventCache(): ForcedEventContextProps {
  const context = useContext(EventCacheContext);

  if (context === undefined) {
    throw new Error('useEventCache must be used within a EventCacheProvider');
  }

  if (context.event === null) {
    throw new Error('useForcedEventCache called but event is null');
  }

  return context as ForcedEventContextProps;
}
